import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { TrialStatusBanner } from "@/components/trial/TrialStatusBanner";
import { TrialDashboard } from "@/components/trial/TrialDashboard";
import { OnboardingFlow } from "@/components/trial/OnboardingFlow";
import { AchievementSystem } from "@/components/trial/AchievementSystem";
import { ConversionModal } from "@/components/trial/ConversionModal";
import { useTrialManagement } from "@/hooks/useTrialManagement";
import { useWorkspace } from "@/hooks/useWorkspace";
import { 
  BarChart3, 
  Trophy, 
  BookOpen, 
  Target,
  Sparkles
} from "lucide-react";

export const TrialManagementPage: React.FC = () => {
  const { currentWorkspace } = useWorkspace();
  const { personalizedOffer } = useTrialManagement();
  const [showConversionModal, setShowConversionModal] = useState(false);

  // Don't show trial management for converted workspaces
  if (!currentWorkspace?.trial_expires_at || currentWorkspace?.trial_status === 'converted') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <Sparkles className="h-12 w-12 text-primary mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Welcome to Your Full Account!
          </h1>
          <p className="text-muted-foreground">
            You're all set with unlimited access to all features.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Trial Center</h1>
          <p className="text-muted-foreground">
            Make the most of your trial with guided onboarding and achievement tracking
          </p>
        </div>
        <Button 
          onClick={() => setShowConversionModal(true)}
          className="bg-primary hover:bg-primary/90"
        >
          Upgrade Now
        </Button>
      </div>

      <TrialStatusBanner />

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="onboarding" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Quick Start
          </TabsTrigger>
          <TabsTrigger value="achievements" className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Achievements
          </TabsTrigger>
          <TabsTrigger value="goals" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Goals
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          <TrialDashboard />
        </TabsContent>

        <TabsContent value="onboarding" className="mt-6">
          <OnboardingFlow />
        </TabsContent>

        <TabsContent value="achievements" className="mt-6">
          <AchievementSystem />
        </TabsContent>

        <TabsContent value="goals" className="mt-6">
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Goal Tracking</h3>
            <p className="text-muted-foreground">
              Set and track your trial goals - coming soon!
            </p>
          </div>
        </TabsContent>
      </Tabs>

      <ConversionModal
        isOpen={showConversionModal}
        onOpenChange={setShowConversionModal}
        offer={personalizedOffer}
      />
    </div>
  );
};